// Colonna «Canale» col logo del marketplace (#6, richiesta Davide 21/07).
//
// ponytail: il componente `MarketplaceLogo` esiste già in shared/ui (loghi
// ufficiali simple-icons + lettermark curati, usato da Ordini e Messaggi).
// Qui non si ridisegna nulla: si traduce solo il `channel` della fatturazione
// nella chiave che quel componente si aspetta.
//
// Il canale di fatturazione porta il PAESE nello slug (`metro_it` vs
// `metro_es`, `leroy_merlin_fr`): è la stessa insegna su mercati diversi, e la
// distinzione conta per il fiscale (IT → SF, ES/FR → OSS). Quindi si stacca il
// suffisso per risolvere il logo, ma il paese resta VISIBILE accanto al nome:
// nasconderlo perderebbe informazione proprio in una pagina fiscale.
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";

// Suffissi paese usati negli slug canale (ISO2 dei mercati su cui vendiamo).
const PAESI = new Set(["it", "es", "fr", "de", "pt", "nl", "be", "at", "pl"]);

// Nomi leggibili per gli slug che MarketplaceLogo non etichetta da solo
// (i Mirakl arrivano come insegna, non come `mirakl`).
const NOMI: Record<string, string> = {
  maisons_du_monde: "Maisons du Monde",
  leroy_merlin: "Leroy Merlin",
  venteunique: "Vente-Unique",
  bricobravo: "Brico Bravo",
  manomano: "ManoMano",
  metro: "Metro",
  woocommerce: "Bob Gardens",
  decathlon: "Decathlon",
  carrefour: "Carrefour",
  mediamarkt: "MediaMarkt",
  temu: "Temu",
};

export interface CanaleRisolto {
  /** Chiave insegna, senza paese (es. `metro`). */
  insegna: string;
  /** Nome leggibile (es. «Metro»). */
  nome: string;
  /** ISO2 maiuscolo, o "" se il canale non ne porta uno. */
  paese: string;
}

/** Scompone lo slug canale in insegna + paese. `metro_es` → {metro, ES}. */
export function risolviCanale(channel: string): CanaleRisolto {
  const slug = (channel || "").trim().toLowerCase();
  if (!slug) return { insegna: "", nome: "—", paese: "" };
  const parti = slug.split("_");
  const ultimo = parti[parti.length - 1] ?? "";
  // Solo se restano token davanti: uno slug che è SOLO un codice paese non è
  // un canale, e mangiarlo lascerebbe l'insegna vuota.
  const haPaese = parti.length > 1 && PAESI.has(ultimo);
  const insegna = haPaese ? parti.slice(0, -1).join("_") : slug;
  return {
    insegna,
    nome: NOMI[insegna] ?? insegna.replace(/_/g, " "),
    paese: haPaese ? ultimo.toUpperCase() : "",
  };
}

/** Canale del sito: lì l'insegna è sempre la stessa e non informa. */
const CANALE_SITO = "woocommerce";

export function ChannelCell({
  channel,
  paymentMethod,
}: {
  channel: string;
  paymentMethod?: string | null;
}) {
  if (!channel) return <span className="text-ink-2">—</span>;
  const { insegna, nome, paese } = risolviCanale(channel);
  // 🔑 Sul SITO il canale è «Bob Gardens» su OGNI riga: una colonna che ripete
  // lo stesso valore non dice niente. Quello che manca lì è COME hanno pagato
  // (richiesta Davide 03/08) — ed è la stessa informazione che il campo
  // «Riferimento» del documento già porta per questo canale (regola 28/07).
  // Sui marketplace il canale resta: lì cambia riga per riga e decide il fiscale.
  if (insegna === CANALE_SITO && paymentMethod) {
    return (
      <span className="flex min-w-0 items-center gap-1.5" title={`${nome} · ${paymentMethod}`}>
        <MarketplaceLogo source={insegna} label={nome} size={16} />
        <span className="truncate text-ink-2">{paymentMethod}</span>
      </span>
    );
  }
  // «generic» = riga senza canale riconosciuto (import storico, riga manuale):
  // dirlo esplicitamente è più utile del punto interrogativo del fallback.
  if (insegna === "generic" || insegna === "manuale") {
    return (
      <span className="block truncate text-ink-2" title={channel}>
        {insegna === "manuale" ? "Manuale" : "Senza canale"}
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={channel}>
      <MarketplaceLogo source={insegna} label={nome} size={16} />
      <span className="truncate text-ink-2">{nome}</span>
      {paese && (
        <span className="shrink-0 rounded bg-paper-2 px-1 text-[10px] font-medium text-ink-2">
          {paese}
        </span>
      )}
    </span>
  );
}
